import React, { useEffect, useState } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import {
  FaYoutube,
  FaPlus,
  FaEdit,
  FaTrash,
  FaTimes,
  FaSave,
  FaLink,
  FaSpinner,
} from "react-icons/fa";
import "./Youtube.css";
import { BASE_URL } from "../../Config/config";

export default function Youtube() {
  const [videos, setVideos] = useState([]);

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [title, setTitle] = useState("");
  const [youtubeUrl, setYoutubeUrl] = useState("");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  /* =========================================================
     YOUTUBE EMBED URL
  ========================================================= */

  const getYoutubeEmbedUrl = (url) => {
    if (!url) return "";

    try {
      const parsedUrl = new URL(url);

      /* youtube.com/watch?v=VIDEO_ID */

      if (
        parsedUrl.hostname === "youtube.com" ||
        parsedUrl.hostname === "www.youtube.com" ||
        parsedUrl.hostname === "m.youtube.com"
      ) {
        const videoId = parsedUrl.searchParams.get("v");

        if (videoId) {
          return `https://www.youtube.com/embed/${videoId}`;
        }

        /* youtube.com/shorts/VIDEO_ID */

        if (parsedUrl.pathname.startsWith("/shorts/")) {
          const videoId = parsedUrl.pathname.split("/shorts/")[1];

          if (videoId) {
            return `https://www.youtube.com/embed/${videoId}`;
          }
        }

        /* youtube.com/embed/VIDEO_ID */

        if (parsedUrl.pathname.startsWith("/embed/")) {
          const videoId = parsedUrl.pathname.split("/embed/")[1];

          if (videoId) {
            return `https://www.youtube.com/embed/${videoId}`;
          }
        }
      }

      /* youtu.be/VIDEO_ID */

      if (
        parsedUrl.hostname === "youtu.be" ||
        parsedUrl.hostname === "www.youtu.be"
      ) {
        const videoId = parsedUrl.pathname.substring(1);

        if (videoId) {
          return `https://www.youtube.com/embed/${videoId}`;
        }
      }
    } catch (error) {
      return "";
    }

    return "";
  };

  /* =========================================================
     FETCH ALL VIDEOS
  ========================================================= */

  const fetchVideos = async () => {
    try {
      setLoading(true);

      const response = await fetch(
        `${BASE_URL}/youtube.php?action=get_youtube_videos`,
      );

      if (!response.ok) {
        throw new Error(`HTTP Error: ${response.status}`);
      }

      const result = await response.json();

      if (result.status) {
        setVideos(result.data || []);
        setCurrentPage(1);
      } else {
        alert(result.message || "Failed to fetch videos");
      }
    } catch (error) {
      console.error("Fetch YouTube videos error:", error);

      alert("Unable to load YouTube videos. Please check your backend/API.");
    } finally {
      setLoading(false);
    }
  };

  /* =========================================================
     LOAD DATA WHEN PAGE OPENS
  ========================================================= */

  useEffect(() => {
    fetchVideos();
  }, []);

  /* =========================================================
     SAVE VIDEO
  ========================================================= */

  const handleSave = async () => {
    if (!title.trim()) {
      alert("Please enter video title");
      return;
    }

    if (!youtubeUrl.trim()) {
      alert("Please enter YouTube video link");
      return;
    }

    const embedUrl = getYoutubeEmbedUrl(youtubeUrl);

    if (!embedUrl) {
      alert("Please enter a valid YouTube URL");
      return;
    }

    try {
      setSaving(true);

      /* =====================================================
         UPDATE
      ===================================================== */

      if (editingId) {
        const response = await fetch(
          `${BASE_URL}/youtube.php?action=update_youtube_video`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              id: editingId,
              title: title.trim(),
              youtubeUrl: youtubeUrl.trim(),
            }),
          },
        );

        if (!response.ok) {
          throw new Error(`HTTP Error: ${response.status}`);
        }

        const result = await response.json();

        if (!result.status) {
          alert(result.message || "Failed to update video");
          return;
        }

        alert("YouTube video updated successfully");
      } else {
        /* =====================================================
         ADD
      ===================================================== */
        const response = await fetch(
          `${BASE_URL}/youtube.php?action=add_youtube_video`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              title: title.trim(),
              youtubeUrl: youtubeUrl.trim(),
            }),
          },
        );

        if (!response.ok) {
          throw new Error(`HTTP Error: ${response.status}`);
        }

        const result = await response.json();

        if (!result.status) {
          alert(result.message || "Failed to add video");
          return;
        }

        alert("YouTube video added successfully");
      }

      /* Refresh database data */

      await fetchVideos();

      resetForm();
    } catch (error) {
      console.error("Save YouTube video error:", error);

      alert("Unable to save YouTube video. Please check your backend.");
    } finally {
      setSaving(false);
    }
  };

  /* =========================================================
     EDIT
  ========================================================= */

  const handleEdit = (video) => {
    setEditingId(video.id);
    setTitle(video.title);
    setYoutubeUrl(video.youtubeUrl);
    setShowForm(true);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };
  useEffect(() => {
    AOS.init({
      duration: 700,
      easing: "ease-out-cubic",
      once: true,
      offset: 80,
    });
  }, []);
  /* =========================================================
     DELETE
  ========================================================= */

  const handleDelete = async (id) => {
    if (
      !window.confirm("Are you sure you want to delete this YouTube video?")
    ) {
      return;
    }

    try {
      setDeletingId(id);

      const response = await fetch(
        `${BASE_URL}/youtube.php?action=delete_youtube_video&id=${id}`,
        {
          method: "DELETE",
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP Error: ${response.status}`);
      }

      const result = await response.json();

      if (!result.status) {
        alert(result.message || "Failed to delete video");
        return;
      }

      setVideos((prev) => prev.filter((video) => video.id !== id));

      alert("YouTube video deleted successfully");
    } catch (error) {
      console.error("Delete YouTube video error:", error);

      alert("Unable to delete YouTube video. Please check your backend.");
    } finally {
      setDeletingId(null);
    }
  };

  /* =========================================================
     RESET FORM
  ========================================================= */

  const resetForm = () => {
    setTitle("");
    setYoutubeUrl("");
    setEditingId(null);
    setShowForm(false);
  };

  /* =========================================================
     OPEN ADD FORM
  ========================================================= */

  const openAddForm = () => {
    setTitle("");
    setYoutubeUrl("");
    setEditingId(null);
    setShowForm(true);
  };

  /* =========================================================
     RENDER
  ========================================================= */
  const totalPages = Math.ceil(videos.length / itemsPerPage);

  const startIndex = (currentPage - 1) * itemsPerPage;

  const currentVideos = videos.slice(startIndex, startIndex + itemsPerPage);

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };
  return (
    <div className="youtube-page">
      {/* =====================================================
          HEADER
      ===================================================== */}

      <div
        className="youtube-header"
        data-aos="fade-down"
        data-aos-duration="700"
      >
        <div>
          <div className="youtube-title">
            <FaYoutube />

            <div>
              <h1>YouTube Videos</h1>

              <p>Manage YouTube videos displayed on your website</p>
            </div>
          </div>
        </div>

        <button
          className="add-youtube-btn"
          onClick={openAddForm}
          disabled={saving}
        >
          <FaPlus />
          Add YouTube Video
        </button>
      </div>

      {/* =====================================================
          STATISTICS
      ===================================================== */}

      <div className="youtube-stats" data-aos="fade-up" data-aos-delay="100">
        <div className="youtube-stat-card">
          <div className="stat-icon">
            <FaYoutube />
          </div>

          <div>
            <span>Total Videos</span>

            <strong>{videos.length}</strong>
          </div>
        </div>
      </div>

      {/* =====================================================
          ADD / EDIT FORM
      ===================================================== */}

      {showForm && (
        <div
          className="youtube-form-card"
          data-aos="fade-down"
          data-aos-duration="700"
        >
          <div className="form-header">
            <div>
              <h2>{editingId ? "Edit YouTube Video" : "Add YouTube Video"}</h2>

              <p>
                Add a YouTube video link that you want to display on your
                website.
              </p>
            </div>

            <button
              className="close-form-btn"
              onClick={resetForm}
              disabled={saving}
            >
              <FaTimes />
            </button>
          </div>

          <div className="youtube-form">
            {/* =================================================
                TITLE
            ================================================= */}

            <div className="form-group1">
              <label>Video Title</label>

              <input
                type="text"
                placeholder="Enter video title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                disabled={saving}
              />
            </div>

            {/* =================================================
                YOUTUBE URL
            ================================================= */}

            <div className="form-group1">
              <label>YouTube Video Link</label>

              <div className="url-input-wrapper">
                <FaLink />

                <input
                  type="text"
                  placeholder="https://www.youtube.com/watch?v=..."
                  value={youtubeUrl}
                  onChange={(e) => setYoutubeUrl(e.target.value)}
                  disabled={saving}
                />
              </div>

              <small>Example: https://www.youtube.com/watch?v=VIDEO_ID</small>
            </div>

            {/* =================================================
                PREVIEW
            ================================================= */}

            {youtubeUrl && getYoutubeEmbedUrl(youtubeUrl) && (
              <div className="youtube-preview">
                <label>Video Preview</label>

                <div className="video-preview-wrapper">
                  <iframe
                    src={getYoutubeEmbedUrl(youtubeUrl)}
                    title="YouTube Preview"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              </div>
            )}

            {/* =================================================
                FORM BUTTONS
            ================================================= */}

            <div className="form-actions">
              <button
                type="button"
                className="cancel-btn"
                onClick={resetForm}
                disabled={saving}
              >
                <FaTimes />
                Cancel
              </button>

              <button
                type="button"
                className="save-youtube-btn"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <FaSpinner className="spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <FaSave />

                    {editingId ? "Update Video" : "Save Video"}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =====================================================
          VIDEO LIST
      ===================================================== */}

      <div
        className="youtube-list-section"
        data-aos="fade-up"
        data-aos-duration="800"
      >
        <div className="section-heading">
          <div>
            <h2>Uploaded Videos</h2>

            <p>All YouTube videos added to your website</p>
          </div>
        </div>

        {/* ===================================================
            LOADING
        =================================================== */}

        {loading ? (
          <div className="empty-youtube">
            <FaSpinner className="spin" />

            <h3>Loading Videos...</h3>

            <p>Please wait while videos are loading.</p>
          </div>
        ) : videos.length === 0 ? (
          /* =================================================
             EMPTY
          ================================================= */

          <div className="empty-youtube">
            <FaYoutube />

            <h3>No YouTube Videos</h3>

            <p>Add your first YouTube video to display it on your website.</p>

            <button onClick={openAddForm}>
              <FaPlus />
              Add Video
            </button>
          </div>
        ) : (
          /* =================================================
             VIDEOS
          ================================================= */

          <div className="youtube-grid">
            {currentVideos.map((video, index) => (
              <div
                className="youtube-card"
                key={video.id}
                data-aos="fade-up"
                data-aos-delay={index * 100}
                data-aos-duration="700"
              >
                {/* VIDEO */}

                <div className="youtube-card-video">
                  <iframe
                    src={video.embedUrl}
                    title={video.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>

                {/* CONTENT */}

                <div className="youtube-card-content">
                  <h3>{video.title}</h3>

                  <p className="youtube-link">
                    <FaLink />

                    {video.youtubeUrl}
                  </p>

                  {/* ACTIONS */}

                  <div className="youtube-card-actions">
                    <button
                      className="edit-video-btn"
                      onClick={() => handleEdit(video)}
                      disabled={saving || deletingId === video.id}
                    >
                      <FaEdit />
                      Edit
                    </button>

                    <button
                      className="delete-video-btn"
                      onClick={() => handleDelete(video.id)}
                      disabled={deletingId === video.id}
                    >
                      {deletingId === video.id ? (
                        <>
                          <FaSpinner className="spin" />
                          Deleting...
                        </>
                      ) : (
                        <>
                          <FaTrash />
                          Delete
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        {totalPages > 1 && (
          <div className="youtube-pagination">
            <button
              type="button"
              className="youtube-pagination-btn"
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Previous
            </button>

            <div className="youtube-pagination-pages">
              {Array.from({ length: totalPages }, (_, index) => index + 1).map(
                (page) => (
                  <button
                    type="button"
                    key={page}
                    className={`youtube-pagination-page ${
                      currentPage === page ? "active" : ""
                    }`}
                    onClick={() => goToPage(page)}
                  >
                    {page}
                  </button>
                ),
              )}
            </div>

            <button
              type="button"
              className="youtube-pagination-btn"
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
